import { useFormikContext } from "formik";

import { Checkbox, Switch } from "components/form/elements";
import { Accordion } from "components/layout";
import { unique } from "utils/array";
import { classes } from "utils/classes";

import { TEXTS } from "../constants";
import { TiVoTargetsListProps, TiVoTargetsForm } from "../types";
import { useTiVoDeviceData } from "../useTiVoDeviceData";
import { getSelectedPlatformTypes } from "../utils";
import styles from "./TargetScrollList.scss";

const GROUP_MAP = {
  oems: "oemGroups",
  clientTypes: "clientGroups",
} as const;

interface TargetScrollListProps extends TiVoTargetsListProps {
  filteredItems: Record<string, string[]>;
}

export function TargetScrollList({
  listName,
  items,
  filteredItems,
  restrictTargets,
  dataTestId = "target-list",
  enableSlugBulkSelection = false,
}: TargetScrollListProps) {
  const { values, setValues, setFieldValue } =
    useFormikContext<TiVoTargetsForm>();
  const {
    getClientTypes,
    getClientTypeIds,
    getDeviceLabel,
    getDeviceSlug,
    getOems,
    getDevicesBySlug,
    getPlatformType,
  } = useTiVoDeviceData();

  const groupKey = GROUP_MAP[listName];
  const visibleGroups = Object.keys(filteredItems);
  const groupIds = Object.keys(items);
  const itemIds = groupIds.flatMap((groupId) => items[groupId] || []);
  const platformTypes = getSelectedPlatformTypes(values, getPlatformType);

  const hasValidPlatformType = (deviceId: string) =>
    platformTypes.length === 0 ||
    platformTypes.includes(getPlatformType(deviceId));

  // Bulk mode: returns IDs with same slug, limited to active list level (group keys or item values)
  const getGroupedDevices = (
    deviceId: string,
    allowedIds: string[]
  ): string[] => {
    if (!enableSlugBulkSelection) {
      return [deviceId];
    }
    const grouped = getDevicesBySlug(getDeviceSlug(deviceId));
    if (grouped.length === 0) {
      return [deviceId];
    }
    const scopedIds = grouped.filter((id) => allowedIds.includes(id));
    return scopedIds.length ? scopedIds : [deviceId];
  };

  /**
   * Bulk mode affects the add flow when enabled. In bulk mode adding item adds all items with the same slug
   * Cases:
   * 1. CLIENT
   *  - we add new client value
   * 2. OEM
   *  - we add new oem
   *  & all clients for this oem
   *  & all client groups
   */
  function handleAddValues(itemId: string) {
    const selectedDeviceIds = getGroupedDevices(itemId, itemIds);
    const newValues = unique(values[listName], selectedDeviceIds);

    if (listName === "clientTypes") {
      setFieldValue(listName, newValues);
    }
    if (listName === "oems") {
      const clients = getClientTypes(
        groupIds,
        selectedDeviceIds,
        restrictTargets
      );

      setValues({
        ...values,
        oems: newValues,
        clientTypes: unique(
          values["clientTypes"],
          Object.values(clients).flat()
        ),
        clientGroups: unique(values["clientGroups"], Object.keys(clients)),
      });
    }
  }

  /**
   * Bulk mode affects the remove flow when enabled. In bulk mode removing item removes all items with the same slug
   * Cases:
   * 1. CLIENT
   *  - we remove client value
   *  & group it belongs to
   *  - if client group is empty, we should also remove oem group (as "all OEMs" no longer applies)
   * 2. OEM
   *  - we remove oem
   *  & its group
   *  & all clients for this oem
   *  & all client groups
   */
  function handleRemoveValues(groupId: string, itemId: string) {
    const groupedItemIds = getGroupedDevices(itemId, itemIds);
    const groupedGroupIds = getGroupedDevices(groupId, groupIds);
    const newValues = values[listName].filter(
      (v) => !groupedItemIds.includes(v)
    );

    if (listName === "clientTypes") {
      let oemGroups = values["oemGroups"];

      if (!clientTypesSelected(groupId, newValues)) {
        const oems = getOems();
        const platformIds = Object.keys(oems).filter((p) =>
          oems[p].some((deviceId) => groupedGroupIds.includes(deviceId))
        );
        oemGroups = oemGroups.filter((v) => !platformIds.includes(v));
      }

      setValues({
        ...values,
        oemGroups,
        clientTypes: newValues,
        clientGroups: values["clientGroups"].filter(
          (v) => !groupedGroupIds.includes(v)
        ),
      });
    }

    if (listName === "oems") {
      const [selectedPlatforms, selectedOems] = [
        enableSlugBulkSelection ? undefined : groupedGroupIds,
        groupedItemIds,
      ];

      const allOems = getOems();
      const oemGroups = Object.keys(allOems).filter((p) =>
        allOems[p].some((oemId) => selectedOems.includes(oemId))
      );

      const clients = getClientTypes(
        selectedPlatforms,
        selectedOems,
        restrictTargets
      );
      const clientIds = Object.values(clients).flat();
      const clientGroups = unique(Object.keys(clients));

      setValues({
        ...values,
        oems: newValues,
        clientTypes: values["clientTypes"].filter(
          (v) => !clientIds.includes(v)
        ),
        oemGroups: values["oemGroups"].filter((v) => !oemGroups.includes(v)),
        clientGroups: values["clientGroups"].filter(
          (v) => !clientGroups.includes(v)
        ),
      });
    }
  }

  function handleGroupChange(groupId: string, isChecked: boolean) {
    if (isChecked) {
      const devices = getGroupedDevices(groupId, groupIds);
      setFieldValue(
        groupKey,
        values[groupKey].filter((v) => !devices.includes(v))
      );
    } else {
      if (listName === "oems") {
        const platformIds = getGroupedDevices(groupId, groupIds);
        const oemIds = platformIds.reduce((arr, platformId) => {
          (items[platformId] || []).forEach((oemId) => {
            const bulkOemIds = getGroupedDevices(oemId, itemIds);

            arr.push(...bulkOemIds);
          });

          return arr;
        }, [] as string[]);

        const clientTypeIds = getClientTypeIds(
          groupIds,
          oemIds,
          restrictTargets
        );
        setValues({
          ...values,
          oems: unique(values["oems"], oemIds),
          oemGroups: unique(values["oemGroups"], platformIds),
          clientTypes: unique(values["clientTypes"], clientTypeIds),
          clientGroups: unique(values["clientGroups"], oemIds),
        });
      }
      if (listName === "clientTypes") {
        const oemIds = getGroupedDevices(groupId, groupIds);
        const clientTypes = oemIds.reduce((arr, oemId) => {
          arr.push(...(items[oemId] || []));
          return arr;
        }, [] as string[]);

        setValues({
          ...values,
          clientTypes: unique(values["clientTypes"], clientTypes),
          clientGroups: unique(values["clientGroups"], oemIds),
        });
      }
    }
  }

  // null - means that there are no group items
  function clientTypesSelected(
    group: string,
    values: string[],
    parentGroup?: string
  ) {
    const clientTypeIds = getClientTypeIds(
      parentGroup ? [parentGroup] : undefined,
      [group]
    );
    if (!clientTypeIds.length) {
      return null;
    }
    return clientTypeIds.some((v) => values?.includes(v));
  }

  return (
    <ul className={styles.scrollList}>
      {visibleGroups.map((groupId) => {
        const groupLength = items[groupId].length;
        const groupEmpty = groupLength === 0;
        const groupInvalid = !hasValidPlatformType(groupId);
        const selected = items[groupId].reduce(
          (acc, item) => acc + (values[listName].includes(item) ? 1 : 0),
          0
        );
        const isAllChecked = values[groupKey].includes(groupId) || groupEmpty;
        const expanded = visibleGroups.length === 1 && !groupEmpty;

        return (
          <li
            key={`target-${listName}-${groupId}`}
            className={styles.scrollGroup}
            data-test-id={`${dataTestId}-group`}
          >
            <Accordion
              disabled={groupEmpty || groupInvalid}
              header={() => (
                <div className={styles.header}>
                  <Switch
                    name={groupKey}
                    id={groupId}
                    value={groupId}
                    checked={isAllChecked}
                    disabled={groupEmpty || groupInvalid}
                    className={styles.switch}
                    dataTestId={`${dataTestId}-all-switch`}
                    onChange={() => handleGroupChange(groupId, isAllChecked)}
                  />
                  <span className={styles.label}>
                    {isAllChecked
                      ? TEXTS.GROUP_ALL.replace(
                          "#{group}",
                          getDeviceLabel(groupId)
                        )
                      : getDeviceLabel(groupId)}
                  </span>
                  <span className={styles.count}>
                    {selected} of {groupLength}
                  </span>
                </div>
              )}
              headerType="compact"
              initialExpanded={expanded}
            >
              {filteredItems[groupId].length
                ? filteredItems[groupId].map((itemId) => {
                    const isChecked =
                      values[listName]?.includes(itemId) || false;
                    const itemsSelected =
                      listName === "oems"
                        ? clientTypesSelected(
                            itemId,
                            values["clientTypes"],
                            groupId
                          )
                        : null;
                    const willBeRemoved = isChecked && itemsSelected === false;
                    const willBeAdded = !isChecked && itemsSelected === true;
                    const itemInvalid = !hasValidPlatformType(itemId);
                    return (
                      <Checkbox
                        label={getDeviceLabel(itemId)}
                        className={classes(styles.scrollItem, {
                          [styles.removed]: willBeRemoved,
                          [styles.added]: willBeAdded,
                        })}
                        name={listName}
                        id={itemId}
                        key={itemId}
                        value={itemId}
                        checked={isChecked}
                        disabled={itemInvalid}
                        indeterminate={willBeRemoved}
                        dataTestId={`${dataTestId}-item`}
                        onChange={() =>
                          isChecked
                            ? handleRemoveValues(groupId, itemId)
                            : handleAddValues(itemId)
                        }
                      />
                    );
                  })
                : "-"}
            </Accordion>
          </li>
        );
      })}
    </ul>
  );
}
